import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISaveProgramFunction } from "../../interfaces/services";
import { ISelectedData, IProgramFunctionForTree } from "../../interfaces/tree";

import useToast from "../useToast";

import { saveProgramFunction } from "../../services/program_functions";

export interface IPayload {
    payload: ISaveProgramFunction;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    selected: ISelectedData | null;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
};

export interface IResponse {
    error?: string;
    message: string;
    statusCode: number;
    data: IProgramFunctionForTree;
};

const useSaveProgramFunction = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const mutationFn = async (data: IPayload): Promise<IResponse> => {
        return await saveProgramFunction(data.payload);
    };

    return useMutation({
        mutationFn,
        onSuccess: (response: IResponse, variables: IPayload) => {
            if (response?.statusCode === 200) {
                addToast({
                    type: 'success',
                    title: 'Program function saved successfully.',
                });

                if (variables.selected) {
                    variables.setSelected({
                        id: response.data.id,
                        level: 2,
                        data: {
                            ...variables.selected.data,
                            id: response.data.id,
                            component_id: response.data.component_id,
                            name: response.data.name,
                            description: response.data.description,
                            parameters: response.data.parameters,
                        }
                    });
                };

                queryClient.invalidateQueries({ queryKey: ['programs_tree'] });
            };
        },
        onError: (error: IResponse, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error.message
            });
        },
        onSettled: (data: IResponse | undefined, error: IResponse | null, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useSaveProgramFunction;